import { useEffect, useState } from 'react';
import {
  ArrowUpRight,
  Calendar,
  Check,
  Clock,
  MapPin,
  Phone,
  Stethoscope,
  Timer,
} from 'lucide-react';
import moment from 'moment';
import { AddToCalendar } from './AddToCalendar';
import { ConfirmationBadge } from './ConfirmationBadge';
import { COUNTDOWN_TICK_MS, PORTAL_ROUTES } from 'utils/constants';
import { formatDate, stripHTML } from 'utils/helpers';
import { getConfirmationState } from './helper';
import { getPractice } from 'library/auth';
import { LocationLink } from 'components/LocationLink';

interface NextAppointmentCardProps {
  appointment: any;
  confirmingId: string | null;
  onConfirm: (id: string) => void;
}

const formatTimeRemaining = (ms: number): string => {
  if (ms <= 0) return 'Happening now';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes / 60) % 24);
  const minutes = totalMinutes % 60;
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
};

export const NextAppointmentCard = ({
  appointment,
  confirmingId,
  onConfirm,
}: NextAppointmentCardProps) => {
  // const practiceInfo = useSelector((s: RootState) => s.patientAuth.practiceInfo);

  // Tick once per minute so the countdown stays current. Local to this card
  // so the rest of the page doesn't re-render every minute. Skipped entirely
  // when there's no appointment to show.
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    if (!appointment) return;
    const id = setInterval(() => setNow(new Date()), COUNTDOWN_TICK_MS);
    return () => clearInterval(id);
  }, [appointment]);

  if (!appointment) {
    return (
      <div className="lg:col-span-2 bg-gray-50 border border-gray-200 rounded-2xl p-5 flex flex-col">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-secondary-400">
          <Calendar size={12} />
          Next Appointment
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
          <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center mb-2">
            <Calendar size={16} className="text-secondary-400" />
          </div>
          <p className="text-sm font-semibold text-secondary-700">No upcoming appointments</p>
          <p className="text-xs text-secondary-500 mt-1">Schedule a visit to see it here.</p>
        </div>
      </div>
    );
  }

  const start = moment(appointment.appointmentAt, "YYYY-MM-DDTHH:mm:ss");

  const remainingLabel = start.isValid() ? formatTimeRemaining(start.diff(now)) : null;
  const isConfirming = confirmingId === appointment.id;
  const confirmationState = getConfirmationState(appointment);
  const practiceInfo = getPractice()

  return (
    <div className="lg:col-span-2 relative overflow-visible rounded-2xl bg-gradient-to-br from-teamcare-blue to-teamcare-blue-dark text-white p-5">
      {/* Top row: label + countdown (left) + status / actions (right) */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-white/70">
            <Calendar size={12} />
            Next Appointment
          </div>
          {remainingLabel && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-white/15 backdrop-blur-sm rounded-md px-2 py-0.5">
              <Timer size={11} />
              {remainingLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {['confirmed', 'pending-request'].includes(confirmationState) && (
            <ConfirmationBadge appt={appointment} />
          )}
          {confirmationState === 'pending' && (
            <button
              onClick={() => onConfirm(appointment.id)}
              disabled={isConfirming}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-800 bg-green-100 rounded-md px-2.5 py-1 shadow-md hover:bg-green-200 transition-colors disabled:opacity-60 disabled:cursor-wait animate-bounce motion-reduce:animate-none"
            >
              <Check size={12} />
              Confirm Your Appointment
            </button>
          )}
          <LocationLink
            to={`${PORTAL_ROUTES.APPOINTMENTS}/${appointment.id}`}
            aria-label="View appointment details"
            title="View details"
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white transition-colors"
          >
            <ArrowUpRight size={16} />
          </LocationLink>
        </div>
      </div>

      {/* Title — appointment date */}
      <div className="mt-3">
        <h2 className="flex items-center gap-2 text-xl font-bold leading-tight">
          <Calendar size={20} className="text-white/80" />
          {formatDate(appointment.date, 'short')}
        </h2>
      </div>

      {/* Provider | Time */}
      <div className="grid grid-cols-2 gap-4 mt-3">
        <div>
          <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white/60">
            <Stethoscope size={10} />
            {appointment.provider.role || 'Provider'}
          </p>
          <p className="text-sm font-semibold mt-0.5">
            {appointment.provider.name || 'Not assigned yet'}
          </p>
        </div>
        <div>
          <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white/60">
            <Clock size={10} />
            Time
          </p>
          <div className="mt-0.5 text-sm font-semibold">
            <span className="bg-white/15 rounded-md px-2 py-0.5">{appointment.startTime}</span>
          </div>
        </div>
      </div>

      {/* Office address + phone + Add to Calendar */}
      <div className="mt-3 pt-3 border-t border-white/15 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/80">
        {practiceInfo?.address && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={12} className="text-white/60" />
            {stripHTML(practiceInfo.address)}
          </span>
        )}
        {practiceInfo?.phone_number && (
          <a
            href={`tel:${practiceInfo.phone_number}`}
            className="inline-flex items-center gap-1.5 hover:text-white"
          >
            <Phone size={12} className="text-white/60" />
            {practiceInfo.phone_number}
          </a>
        )}
        <div className="ml-auto">
          <AddToCalendar appointment={appointment} variant="ghost" />
        </div>
      </div>
    </div>
  );
};
